if (!customElements.get('wholesale-opener')) {
  customElements.define(
    'wholesale-opener',
    class WholesaleOpener extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button || this.initialized) return;
        this.initialized = true;
        this.button.addEventListener('click', () => this.open());
      }

      get modal() {
        // modal-dialog moves itself to <body>; take the newest match in case the theme editor re-rendered the section.
        return [...document.querySelectorAll(this.dataset.modal)].pop();
      }

      open() {
        const modal = this.modal;
        if (!modal) return;
        const form = modal.querySelector('wholesale-form');
        form?.showForm();
        modal.show(this.button);
        form?.prepareCaptcha();
        form?.focusSuccess();
      }

      close() {
        this.modal?.hide();
      }
    }
  );

  // Open the popup while its block is selected in the theme editor, so it can be previewed.
  document.addEventListener('shopify:block:select', (event) => {
    event.target.querySelector?.('wholesale-opener')?.open();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.querySelector?.('wholesale-opener')?.close();
  });
}

if (!customElements.get('wholesale-form')) {
  customElements.define(
    'wholesale-form',
    class WholesaleForm extends HTMLElement {
      static MAX_QUANTITY = 999999;

      // How long to wait for Shopify's spam protection to load, and for its captcha to start the post, before giving up.
      static CAPTCHA_LOAD_TIMEOUT = 15000;
      static CAPTCHA_SUBMIT_TIMEOUT = 20000;

      // Invisible direction/format marks (LRM, RLM, zero-width space...) that copy-paste on Hebrew devices adds.
      static INVISIBLE_CHARACTERS = /\p{Cf}/gu;

      // At least two letters; otherwise only letters, spaces and name punctuation (apostrophe, geresh, gershayim, dot, dashes).
      static NAME_PATTERN = /^(?=(?:\P{L}*\p{L}){2})[\p{L}\p{M}\p{Pd} '’׳״".]+$/u;

      static EMAIL_PATTERN =
        /^[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9-]{2,59})$/;

      // Israeli numbers in national format: mobile 05X, 07X, landlines 02/03/04/08/09, business lines 1-599/1-700/1-800/1-801.
      static PHONE_PATTERN = /^(?:05\d{8}|07[2-9]\d{7}|0[2-489][2-9]\d{6}|1(?:599|700|80[01])\d{6})$/;

      // A trailing extension ("שלוחה 12", "ext. 12", "#12") is kept for the sales team but not validated.
      static PHONE_EXTENSION = /\s*(?:שלוחה|שלוח['׳]?|ext\.?|x|#|,)\s*\d{1,6}\s*$/iu;

      // Anything a phone number (or its extension) can't be typed with is dropped as the customer types.
      static PHONE_DISALLOWED_CHARACTERS = /[^\d+\s\p{Pd}−().\/#,'׳א-תextEXT]/gu;

      static clean(value) {
        return value.replace(WholesaleForm.INVISIBLE_CHARACTERS, '').replace(/\s+/g, ' ').trim();
      }

      // Arabic-Indic (٠-٩) and Persian (۰-۹) digits from Arabic keyboards and autofill.
      static toAsciiDigits(value) {
        return value
          .replace(/[٠-٩]/g, (digit) => digit.charCodeAt(0) - 0x0660)
          .replace(/[۰-۹]/g, (digit) => digit.charCodeAt(0) - 0x06f0);
      }

      // "+972 (0)50-123-4567", "00972-3-6123456" and "050.123.4567" all become national digits like "0501234567".
      static normalizePhone(value) {
        const compact = WholesaleForm.toAsciiDigits(value)
          .replace(WholesaleForm.INVISIBLE_CHARACTERS, '')
          .replace(WholesaleForm.PHONE_EXTENSION, '')
          .replace(/[\p{Pd}−\s().\/]/gu, '');

        // Palestinian mobiles (059, 056) are often saved with +970.
        const palestinianMobile = compact.match(/^(?:\+|00)?970(0?5[69]\d{7})$/);
        const international = palestinianMobile || compact.match(/^(?:\+|00)?972(\d+)$/);
        if (!international) return compact;
        return international[1].startsWith('0') ? international[1] : `0${international[1]}`;
      }

      static isValidPhone(value) {
        const phone = WholesaleForm.normalizePhone(value);
        return WholesaleForm.PHONE_PATTERN.test(phone) && !/0{7}$/.test(phone);
      }

      static isValidEmail(value) {
        const [localPart = ''] = value.split('@');
        return value.length <= 254 && localPart.length <= 64 && WholesaleForm.EMAIL_PATTERN.test(value);
      }

      connectedCallback() {
        // modal-dialog re-appends itself to <body>, which reconnects this element.
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        this.fieldsStep = this.querySelector('[data-wholesale-fields]');
        this.successStep = this.querySelector('[data-wholesale-success]');
        this.alert = this.querySelector('[data-wholesale-alert]');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.summaryInput = this.querySelector('[data-wholesale-summary]');
        this.totalInput = this.querySelector('[data-wholesale-total-input]');
        this.totalOutput = this.querySelector('[data-wholesale-total]');
        this.quantityInputs = [...this.querySelectorAll('[data-variant-quantity]')];
        this.quantityError = this.querySelector('[data-wholesale-quantities] .wholesale-field__error');
        this.lockedWhileSending = [...this.querySelectorAll('[data-wholesale-lock]')];

        // Capture phase, so this runs before the submit listener Shopify's spam protection (hCaptcha) adds to contact forms.
        this.form.addEventListener('submit', this.onSubmit.bind(this), { capture: true });
        this.form.addEventListener('input', this.onInput.bind(this));
        this.form.addEventListener('change', this.onInput.bind(this));
        this.form.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('click', this.onClick.bind(this));

        // Coming back to this page from the browser's cache: unlock a form that was mid-send when the customer left.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) this.setLoading(false);
        });

        // Policy links in the terms label shouldn't navigate away from a half-filled form.
        this.querySelectorAll('.wholesale-checkbox__text a').forEach((link) => {
          link.target = '_blank';
          link.rel = 'noopener';
        });

        // Rendered disabled so the form can't be sent before this script is running.
        this.submitButton.disabled = false;

        if (this.querySelector('[data-wholesale-autoopen]')) this.openAfterPageSubmit();
      }

      get modal() {
        return this.closest('modal-dialog');
      }

      get captchaBound() {
        return this.form.hasAttribute('data-hcaptcha-bound') || this.form.hasAttribute('data-recaptcha-bound');
      }

      // Shopify's spam protection only starts loading its captcha once a contact form is focused or changed. Nudge it as
      // soon as the popup opens so it's ready by the time the customer sends. Stores without protection ignore this.
      prepareCaptcha() {
        this.form.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onClick(event) {
        const stepButton = event.target.closest('[data-step]');
        if (stepButton) {
          const input = stepButton.closest('.wholesale-qty').querySelector('input');
          this.setQuantity(input, this.getQuantity(input) + Number(stepButton.dataset.step));
          return;
        }

        if (event.target.closest('[data-wholesale-close]')) this.modal?.hide();
      }

      onInput(event) {
        const target = event.target;

        if (target.matches('[data-variant-quantity]')) {
          if (event.type === 'change') {
            this.setQuantity(target, this.getQuantity(target));
          } else {
            const digits = target.value.replace(/\D/g, '');
            if (digits !== target.value) target.value = digits;
            this.updateQuantityState(target);
          }
          return;
        }

        if (event.type === 'input' && target.dataset.validate === 'phone') {
          const allowed = WholesaleForm.toAsciiDigits(target.value).replace(WholesaleForm.PHONE_DISALLOWED_CHARACTERS, '');
          if (allowed !== target.value) target.value = allowed;
        }

        // A flagged field is re-checked as it's edited, so its error clears only once the value is actually valid.
        if (target.getAttribute?.('aria-invalid') === 'true') this.validateField(target);
      }

      onFocusOut(event) {
        const target = event.target;
        // Flag a mistyped name, phone or email as soon as the customer moves on; empty fields wait for submit.
        if (target.matches('.wholesale-field__input[required]') && target.value.trim()) this.validateField(target);
      }

      // Called by the opener: after a successful send, opening the popup again starts a fresh inquiry.
      showForm() {
        if (this.successStep.hidden) return;
        // The send finished while the popup was closed: show its confirmation once before starting over.
        if (!this.successSeen) {
          this.successSeen = true;
          return;
        }
        this.successStep.hidden = true;
        this.fieldsStep.hidden = false;
      }

      focusSuccess() {
        if (!this.successStep.hidden) this.successStep.focus();
      }

      getQuantity(input) {
        return parseInt(input.value, 10) || 0;
      }

      setQuantity(input, quantity) {
        input.value = Math.min(Math.max(quantity, 0), WholesaleForm.MAX_QUANTITY);
        this.updateQuantityState(input);
      }

      updateQuantityState(input) {
        input.closest('.wholesale-variant')?.classList.toggle('is-selected', this.getQuantity(input) > 0);
        const total = this.getTotal();
        if (this.totalOutput) this.totalOutput.textContent = total;
        if (total > 0) this.setQuantityError(null);
      }

      getTotal() {
        return this.quantityInputs.reduce((total, input) => total + this.getQuantity(input), 0);
      }

      // Checks every required field and the quantities; returns false (and focuses the first problem) if anything is wrong.
      validate() {
        const invalid = [...this.form.querySelectorAll('[required]')].filter(
          (input) => !this.validateField(input, { commit: true })
        );

        if (this.getTotal() === 0) {
          this.setQuantityError(this.dataset.errorQuantity);
          invalid.push(this.quantityInputs[0]);
        } else {
          this.setQuantityError(null);
        }

        invalid.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
        invalid[0]?.focus();

        return invalid.length === 0;
      }

      validateField(input, { commit = false } = {}) {
        const message = this.getFieldError(input);
        // Send exactly what was checked: no stray spaces, invisible direction marks or non-ASCII digits.
        if (commit && !message && input.type !== 'checkbox') {
          const value = WholesaleForm.clean(input.value);
          input.value = input.dataset.validate === 'phone' ? WholesaleForm.toAsciiDigits(value) : value;
        }
        this.setError(input, message);
        return !message;
      }

      getFieldError(input) {
        if (input.type === 'checkbox') return input.checked ? null : this.dataset.errorTerms;

        const value = WholesaleForm.clean(input.value);
        if (!value) return this.dataset.errorRequired;

        switch (input.dataset.validate) {
          case 'name':
            return WholesaleForm.NAME_PATTERN.test(value) ? null : this.dataset.errorName;
          case 'email':
            return WholesaleForm.isValidEmail(value) ? null : this.dataset.errorEmail;
          case 'phone':
            return WholesaleForm.isValidPhone(value) ? null : this.dataset.errorPhone;
          default:
            return null;
        }
      }

      setError(input, message) {
        if (message) {
          input.setAttribute('aria-invalid', 'true');
        } else {
          input.removeAttribute('aria-invalid');
        }
        input.closest('.wholesale-field, .wholesale-form__terms')?.classList.toggle('is-invalid', Boolean(message));

        const errorElement = document.getElementById(input.getAttribute('aria-describedby'));
        if (!errorElement) return;
        errorElement.textContent = message || '';
        errorElement.hidden = !message;
      }

      setQuantityError(message) {
        this.quantityInputs.forEach((input) => {
          if (message) {
            input.setAttribute('aria-invalid', 'true');
          } else {
            input.removeAttribute('aria-invalid');
          }
        });
        this.quantityError.textContent = message || '';
        this.quantityError.hidden = !message;
      }

      writeSummary() {
        const { skuLabel, quantityLabel } = this.dataset;

        this.summaryInput.value = this.quantityInputs
          .filter((input) => this.getQuantity(input) > 0)
          .map((input) => {
            const sku = input.dataset.sku ? ` | ${skuLabel}: ${input.dataset.sku}` : '';
            return `• ${input.dataset.label}${sku} | ${quantityLabel}: ${this.getQuantity(input)}`;
          })
          .join('\n');
        this.totalInput.value = this.getTotal();
      }

      async onSubmit(event) {
        // Shopify's spam protection posts contact forms from its own submit listeners and ignores preventDefault(),
        // so stop them: this form is only ever sent from here, after validation.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.alert.hidden = true;
        if (!this.validate()) return;

        this.writeSummary();
        // Locks the fields too, so what gets posted is exactly what was just validated.
        this.setLoading(true);

        // On stores with spam protection (fighter-il has it on), only Shopify's captcha can send the form.
        if (this.form.dataset.cptcha) {
          if (this.captchaBound || (await this.waitForCaptcha())) {
            this.submitThroughCaptcha();
          } else {
            // The captcha couldn't load (blocked or offline): send nothing and keep what the customer typed.
            this.setLoading(false);
            this.alert.hidden = false;
          }
          return;
        }

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
          });
          if (!response.ok || !(await this.isPosted(response))) throw new Error('Wholesale inquiry was not accepted');
          this.showSuccess();
        } catch (error) {
          console.error(error);
          this.alert.hidden = false;
        } finally {
          this.setLoading(false);
        }
      }

      async waitForCaptcha() {
        const start = Date.now();
        while (!this.captchaBound && Date.now() - start < WholesaleForm.CAPTCHA_LOAD_TIMEOUT) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
        return this.captchaBound;
      }

      // Shopify's captcha wrapper (form.submit once bound) solves the captcha, writes its token and posts natively. The
      // product page then reloads with ?contact_posted=true and the popup reopens on the confirmation.
      submitThroughCaptcha() {
        const tokenField = () => this.form.elements['h-captcha-response'] || this.form.elements['g-recaptcha-response'];
        if (tokenField()) tokenField().value = '';
        this.form.submit();

        const start = Date.now();
        const timer = setInterval(() => {
          // A token means the post has started and the page is navigating: stay locked so it can't be sent twice.
          if (tokenField()?.value) return clearInterval(timer);
          if (Date.now() - start < WholesaleForm.CAPTCHA_SUBMIT_TIMEOUT) return;
          // The captcha was closed or failed and nothing was sent: let the customer try again.
          clearInterval(timer);
          this.setLoading(false);
          this.alert.hidden = false;
        }, 250);
      }

      async isPosted(response) {
        if (new URL(response.url).searchParams.get('contact_posted') === 'true') return true;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const success = html.querySelector(`#${this.form.id} [data-wholesale-success]`);
        return Boolean(success && !success.hasAttribute('hidden'));
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.setAttribute('aria-disabled', isLoading);
        this.submitButton.setAttribute('aria-busy', isLoading);
        // inert, not disabled: inert fields are still included in the post.
        this.lockedWhileSending.forEach((element) => {
          element.inert = isLoading;
        });
      }

      showSuccess() {
        this.form.reset();
        this.summaryInput.value = '';
        this.totalInput.value = '';
        this.quantityInputs.forEach((input) => this.setQuantity(input, 0));
        this.fieldsStep.hidden = true;
        this.successStep.hidden = false;
        // If the popup was closed while sending, keep the confirmation for the next time it's opened.
        this.successSeen = Boolean(this.modal?.hasAttribute('open'));
        if (this.successSeen) this.focusSuccess();
      }

      openAfterPageSubmit() {
        const modal = this.modal;
        const opener = document.querySelector(`wholesale-opener[data-modal="#${modal.id}"] button`);
        this.successSeen = true;
        modal.show(opener);
        this.prepareCaptcha();
        this.focusSuccess();

        // Drop ?contact_posted=true so a refresh doesn't reopen the confirmation.
        const url = new URL(window.location.href);
        url.searchParams.delete('contact_posted');
        url.hash = '';
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
  );
}
